import { Snackbar } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  ICommentSideModal,
  ICommentWrapper,
  IGeneralComment,
} from '../../interfaces/components/comment.interface';
import CommentForm from '../comment/CommentForm';
import CommentItem from '../comment/CommentItem';
import CommentSkeleton from '../comment/CommentSkeleton';
import PinnedComment from '../comment/PinnedComment';
import ConfirmModal from '../modal/training-module/ConfirmModal';

const CommentSection: React.FC<ICommentSideModal & ICommentWrapper> = ({
  toast,
  openConfirm,
  useCommentGeneralUtils,
  customDataMap,
  commentSectionId,
}) => {
  const {
    commentList,
    updateComment,
    deleteComment,
    voteComment,
    addComment,
    pinComment,
    infinityQueryReturn: {
      isFetching,
      isLoading,
      fetchNextPage,
      data: fetchedCommentList,
    },
  } = useCommentGeneralUtils.useCommentHook();
  const { commentListMeta, commentSectionRole } =
    useCommentGeneralUtils.useMetaRoleHook(
      fetchedCommentList as unknown as IGeneralComment,
      commentList
    );
  const { ref, inView } = useInView(); //detect if element in the viewport

  useEffect(() => {
    if (inView && !isFetching && commentListMeta?.hasNextPage) {
      fetchNextPage();
    }
  }, [commentListMeta?.hasNextPage, fetchNextPage, inView, isFetching]);

  return (
    <>
      <h6 className=" mb-2 text-xl font-bold leading-[30px] tracking-[-0.5px]">
        Bình luận
      </h6>
      <CommentForm
        placeholder={'Nhập bình luận tại đây'}
        className={'mb-4'}
        toast={toast}
        openConfirm={openConfirm}
        handleAddGlobal={addComment}
        handleUpdateGlobal={updateComment}
        commentSectionId={commentSectionId}
      />
      <PinnedComment
        toast={toast}
        openConfirm={openConfirm}
        useCommentGeneralUtils={useCommentGeneralUtils}
        customDataMap={customDataMap}
        commentSectionId={commentSectionId}
      />

      {commentListMeta?.totalCount > 0 && !isLoading
        ? commentList?.map((comment, key: number) => {
            return (
              <>
                {key ===
                  commentListMeta.pageSize * commentListMeta.currentPage - 4 &&
                commentListMeta.hasNextPage ? (
                  <div className="invisible h-[1px] w-full" ref={ref}></div>
                ) : null}
                {/* thay vì tiếp tục fetch khi ở cuối list, mình tiếp tục fetch ở item thứ 3 từ dưới lên */}
                <CommentItem
                  key={comment?.id}
                  comment={customDataMap(comment, commentSectionRole)}
                  handleTwoWaysPinGlobal={pinComment}
                  handleDeleteGlobal={deleteComment}
                  handleUpdateGlobal={updateComment}
                  handleVoteGlobal={voteComment}
                  handleAddGlobal={addComment}
                  toast={toast}
                  openConfirm={openConfirm}
                  customDataMap={customDataMap}
                  useCommentGeneralUtils={useCommentGeneralUtils}
                  commentSectionId={commentSectionId}
                />
              </>
            );
          })
        : null}

      {isLoading || isFetching ? (
        <div className="flex flex-col gap-8">
          <CommentSkeleton />
          <CommentSkeleton />
          <CommentSkeleton />
        </div>
      ) : null}
    </>
  );
};

const CommentSectionWrapper = (prop: ICommentWrapper) => {
  const [openToast, setOpenToast] = useState<{
    type: 'success' | 'error';
    content: string;
  }>({
    type: 'success',
    content: '',
  });

  const [openConfirm, setOpenConfirm] = useState<{
    title: string;
    description: string;
    onClose: () => void;
    cancelCallback: () => void;
    confirmCallback: () => void;
    isOpen: boolean;
  }>({
    isOpen: false,
    title: '',
    description: '',
    onClose: () => {},
    cancelCallback: () => {},
    confirmCallback: () => {},
  });

  const toast = useCallback(
    (content: string, type: 'success' | 'error' = 'success') => {
      setOpenToast({ type, content });
    },
    []
  );

  const handleOpenConfirm = useCallback(
    (_data: {
      title: string;
      description: string;
      onClose: () => void;
      cancelCallback: () => void;
      confirmCallback: () => void;
      isOpen: boolean;
    }) => {
      setOpenConfirm(_data);
    },
    []
  );

  const commentSectionMemo = useMemo(
    () => (
      <CommentSection toast={toast} openConfirm={handleOpenConfirm} {...prop} />
    ),
    [handleOpenConfirm, prop, toast]
  );

  return (
    <>
      {commentSectionMemo}
      <Snackbar
        autoHideDuration={1500}
        disableWindowBlurListener
        onClose={() => toast('')}
        open={!!openToast.content}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        className={`rounded-md p-4 ${
          openToast.type === 'success'
            ? '!bg-success_secondary !text-success_pressed'
            : '!bg-error_lighter !text-error_pressed'
        }`}
      >
        <div>
          {openToast.type === 'success' ? (
            <div className="flex h-full w-full gap-2">
              <InfoIcon />
              {openToast.content}
            </div>
          ) : (
            <div className="flex h-full w-full gap-2">
              <InfoIcon />
              {openToast.content}
            </div>
          )}
        </div>
      </Snackbar>
      <ConfirmModal
        open={openConfirm.isOpen}
        onClose={() => {
          openConfirm.onClose();
          setOpenConfirm((state) => ({ ...state, isOpen: false }));
        }}
        cancelCallback={() => {
          openConfirm.cancelCallback();
          setOpenConfirm((state) => ({ ...state, isOpen: false }));
        }}
        confirmCallback={openConfirm.confirmCallback}
        title={openConfirm.title}
        description={openConfirm.description}
      />
    </>
  );
};

const InfoIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C10.0222 2 8.08879 2.58649 6.4443 3.6853C4.79981 4.78412 3.51809 6.3459 2.76121 8.17317C2.00433 10.0004 1.8063 12.0111 2.19215 13.9509C2.578 15.8907 3.53041 17.6725 4.92894 19.0711C6.32746 20.4696 8.10929 21.422 10.0491 21.8079C11.9889 22.1937 13.9996 21.9957 15.8268 21.2388C17.6541 20.4819 19.2159 19.2002 20.3147 17.5557C21.4135 15.9112 22 13.9778 22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7363 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2V2ZM12 20C10.4178 20 8.87104 19.5308 7.55544 18.6518C6.23985 17.7727 5.21447 16.5233 4.60897 15.0615C4.00347 13.5997 3.84504 11.9911 4.15372 10.4393C4.4624 8.88743 5.22433 7.46197 6.34315 6.34315C7.46197 5.22433 8.88743 4.4624 10.4393 4.15372C11.9911 3.84504 13.5997 4.00346 15.0615 4.60896C16.5233 5.21447 17.7727 6.23984 18.6518 7.55544C19.5308 8.87103 20 10.4177 20 12C20 14.1217 19.1572 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20V20Z"
      fill="currentColor"
    />
    <path
      d="M12 9C12.5523 9 13 8.55228 13 8C13 7.44772 12.5523 7 12 7C11.4477 7 11 7.44772 11 8C11 8.55228 11.4477 9 12 9Z"
      fill="currentColor"
    />
    <path
      d="M12 10C11.7348 10 11.4804 10.1054 11.2929 10.2929C11.1054 10.4804 11 10.7348 11 11V16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17C12.2652 17 12.5196 16.8946 12.7071 16.7071C12.8946 16.5196 13 16.2652 13 16V11C13 10.7348 12.8946 10.4804 12.7071 10.2929C12.5196 10.1054 12.2652 10 12 10Z"
      fill="currentColor"
    />
  </svg>
);

export default CommentSectionWrapper;
